import React, { useEffect, useRef, useState } from 'react';
import { Form, Button, Modal, Row, Col } from 'react-bootstrap'; // Add Modal here
import { useNavigate } from 'react-router-dom';
import useApplicationStore from '../../store/applicationStore';
import { CREDIT_API_URL } from '../../Constants';
import showToast from '../../utility/showToast';
import CasheLoanPlanModal from './CasheLoanPlanModal'; // Import the new modal component
import ConfirmationModal from '../../utility/ConfirmationModal';
import axios from 'axios'; // Import axios for API calls
// Remove: import NotificationPopup from '../../utility/NotificationPopup'; // REMOVE THIS LINE

const PersonalDetailsStep = ({ onNext, onPrev, onEditStep, isSubmitting = false }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPermanentAddress, setShowPermanentAddress] = useState(false);
  // Remove these states:
  // const [showNotification, setShowNotification] = useState(false);
  // const [notificationMessage, setNotificationMessage] = useState('');
  // const [notificationType, setNotificationType] = useState('success');
  // const [redirectOnNotification, setRedirectOnNotification] = useState(false);

  // Add new states for the rejection modal
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionModalMessage, setRejectionModalMessage] = useState('');
  const [isAllocatingAlternativeLender, setIsAllocatingAlternativeLender] = useState(false);

  const [cashePlans, setCashePlans] = useState([]);
  const [showLoanPlanModal, setShowLoanPlanModal] = useState(false);
  const [preApprovedAmount, setPreApprovedAmount] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isProcessingPlan, setIsProcessingPlan] = useState(false); // New state for processing plan
  
  // Pincode details state
  const [pincodeDistricts, setPincodeDistricts] = useState([]);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [isLoadingPincodeDetails, setIsLoadingPincodeDetails] = useState(false);
  
  // MOVE handleCloseLoanPlanModal HERE
  const handleCloseLoanPlanModal = () => {
    setShowLoanPlanModal(false);
  };

  // Zustand store
  const {
    personalDetails,
    errors,
    leadData,
    updatePersonalDetail,
    setPersonalDetails,
    setErrors,
    isPersonalDetailsValid,
    setLeadData: setStoreLeadData,
    setCurrentStep: setStoreCurrentStep
  } = useApplicationStore();

  // Fetch personal details on component mount
  const hasFetchedPersonalDetailsRef = useRef(false);
  const lastLeadIdRef = useRef(null);
  const hasSyncedAddressRef = useRef(false);
  
  useEffect(() => {
    const loadPersonalDetails = async () => {
      if (leadData && leadData.id) {
        // Reset the fetch flag if leadId has changed
        if (lastLeadIdRef.current !== leadData.id) {
          hasFetchedPersonalDetailsRef.current = false;
          lastLeadIdRef.current = leadData.id;
        }
        
        if (!hasFetchedPersonalDetailsRef.current) {
          hasFetchedPersonalDetailsRef.current = true;
          console.log('Loading personal details for leadId:', leadData.id);
          setIsLoading(true);
        
        try {
          const response = await fetch(`${CREDIT_API_URL}/personal-details?leadId=${leadData.id}`);
          console.log('Personal Details API Response:', response.status);
          
          if (response.ok) {
            const result = await response.json();
            console.log('Personal Details API Response:', result);
            
            if (result.status === 'Success' && result.data) {
              const data = result.data;
              console.log('Processing API data:', data);
              
              // Update store with API data
              const apiPersonalDetails = {
                fullName: data.fullName || '',
                panNumber: data.panNumber || '',
                gender: data.gender ? data.gender.charAt(0).toUpperCase() + data.gender.slice(1).toLowerCase() : 'Male',
                emailId: data.emailId || '',
                aadharNumber: data.aadharNumber || '',
                dateOfBirth: data.dateOfBirth || '',
                mobile: data.mobile || '',
                 // Address fields - use nested structure
                 permanentAddress: data.permanentAddress || {
                   addressLine: '',
                   landmark: '',
                   city: '',
                   state: '',
                   country: 'India',
                   pincode: ''
                 },
                 currentAddress: data.currentAddress ? {
                   ...data.currentAddress,
                   pincode: data.currentAddress.pincode || data.pincode || '' // Use currentAddress.pincode, fallback to root pincode
                 } : {
                   addressLine: '',
                   landmark: '',
                   city: '',
                   state: '',
                   country: 'India',
                   pincode: data.pincode || '' // Use root level pincode if currentAddress is null
                 },
                // Additional fields for current address
                isSameAsCurrent: data.isSameAsPermanent || data.isSameAsCurrent || true,
                currentResidenceDurationYears: data.currentResidenceDurationYears || '',
                currentResidenceType: data.currentResidenceType || ''
              };
              
              console.log('Setting personal details with API data:', apiPersonalDetails);
              
              // If isSameAsCurrent is true, copy currentAddress to permanentAddress
              if (apiPersonalDetails.isSameAsCurrent && apiPersonalDetails.currentAddress) {
                apiPersonalDetails.permanentAddress = {
                  ...apiPersonalDetails.currentAddress
                };
                console.log('Copied currentAddress to permanentAddress since isSameAsCurrent is true');
              }
              
              setPersonalDetails(apiPersonalDetails);
              
              // Check if city/state are present in currentAddress
              const hasCityState = apiPersonalDetails.currentAddress.city && apiPersonalDetails.currentAddress.state;
              const pincodeValue = apiPersonalDetails.currentAddress.pincode;
              
              // If city/state are NOT present and pincode exists, call pincode/details API
              if (!hasCityState && pincodeValue && pincodeValue.length === 6) {
                await fetchPincodeDetails(pincodeValue, apiPersonalDetails.isSameAsCurrent);
              }
              
              // Clear any existing errors
              setErrors('personal', {});
              
              console.log('Personal details loaded successfully from API');
            } else {
              console.log('No personal details found for this lead');
            }
          } else {
            console.log('Failed to load personal details:', response.status);
          }
        } catch (error) {
          console.error('Error loading personal details:', error);
          showToast('Error loading personal details', 'error');
        } finally {
          setIsLoading(false);
        }
        }
      }
    };

    loadPersonalDetails();
  }, [leadData?.id]);

  // Update showPermanentAddress when personalDetails.isSameAsCurrent changes
  useEffect(() => {
    setShowPermanentAddress(!personalDetails.isSameAsCurrent);
    
    // If isSameAsCurrent is true, sync permanentAddress from currentAddress (but only if not already synced)
    if (personalDetails.isSameAsCurrent && personalDetails.currentAddress) {
      const currentAddr = personalDetails.currentAddress;
      const permanentAddr = personalDetails.permanentAddress;
      
      // Only sync if permanentAddress is missing required fields and we haven't synced yet
      const needsSync = (!permanentAddr?.addressLine || 
                        !permanentAddr?.city || 
                        !permanentAddr?.state || 
                        !permanentAddr?.pincode) && !hasSyncedAddressRef.current;
      
      if (needsSync && currentAddr.pincode) {
        updatePersonalDetail('permanentAddress.addressLine', currentAddr.addressLine || '');
        updatePersonalDetail('permanentAddress.city', currentAddr.city || '');
        updatePersonalDetail('permanentAddress.state', currentAddr.state || '');
        updatePersonalDetail('permanentAddress.country', currentAddr.country || 'India');
        updatePersonalDetail('permanentAddress.pincode', currentAddr.pincode || '');
        updatePersonalDetail('permanentAddress.landmark', currentAddr.landmark || '');
        hasSyncedAddressRef.current = true;
      }
    } else {
      // Reset sync flag when isSameAsCurrent is false
      hasSyncedAddressRef.current = false;
    }
  }, [personalDetails.isSameAsCurrent, personalDetails.currentAddress?.pincode, updatePersonalDetail]);

  // Fetch pincode details API
  const fetchPincodeDetails = async (pincode, isSameAsCurrent = false) => {
    if (!pincode || pincode.length !== 6) {
      return;
    }

    setIsLoadingPincodeDetails(true);
    try {
      const response = await fetch(`${CREDIT_API_URL}/pincode/details?pincode=${pincode}`);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Pincode details API response:', result);
        
        if (result.success && result.data && result.data.length > 0) {
          // Get unique districts
          const uniqueDistricts = [...new Set(result.data.map(item => item.district))];
          const stateName = result.data[0].stateName;
          
          setPincodeDistricts(result.data);
          
          // Auto-fill state (read-only)
          updatePersonalDetail('currentAddress.state', stateName);
          
          // If "same as current" is selected, also update permanent address state
          if (isSameAsCurrent) {
            updatePersonalDetail('permanentAddress.state', stateName);
          }
          
          // Handle districts - always show dropdown if districts exist
          if (uniqueDistricts.length === 1) {
            // Single district - auto-select immediately
            const district = uniqueDistricts[0];
            // Set the value synchronously to ensure dropdown shows selected value
            updatePersonalDetail('currentAddress.city', district);
            if (isSameAsCurrent) {
              updatePersonalDetail('permanentAddress.city', district);
            }
            // Keep dropdown visible even for single district
          } else if (uniqueDistricts.length > 1) {
            // Multiple districts - if no city is selected yet, don't auto-select
            // User must choose from dropdown
          }
          // Dropdown will be shown if pincodeDistricts.length > 0
        }
      } else {
        console.log('Failed to fetch pincode details:', response.status);
      }
    } catch (error) {
      console.error('Error fetching pincode details:', error);
    } finally {
      setIsLoadingPincodeDetails(false);
    }
  };

  // Handle district selection from dropdown
  const handleDistrictSelect = (district) => {
    updatePersonalDetail('currentAddress.city', district);
    if (personalDetails.isSameAsCurrent) {
      updatePersonalDetail('permanentAddress.city', district);
    }
    // Don't hide dropdown - keep it visible so user can change selection
    
    // Clear error if exists
    const newErrors = { ...errors.personal };
    delete newErrors['currentAddress.city'];
    if (personalDetails.isSameAsCurrent) {
      delete newErrors['permanentAddress.city'];
    }
    setErrors('personal', newErrors);
  };

  // Validation functions
  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePinCode = (pinCode) => {
    const pinRegex = /^[0-9]{6}$/;
    const trimmedPinCode = pinCode ? pinCode.toString().trim() : '';
    return pinRegex.test(trimmedPinCode);
  };

  const validateGender = (gender) => {
    return ['Male', 'Female', 'Other'].includes(gender);
  };

  const validateAadhar = (aadhar) => {
    const aadharRegex = /^[0-9]{12}$/;
    return aadharRegex.test(aadhar);
  };

  const validateDateOfBirth = (dateOfBirth) => {
    if (!dateOfBirth) return false;
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Check if birthday hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Age should be between 18 and 100
    return age >= 18 && age <= 100;
  };

  const validateStayingDuration = (duration) => {
    const numDuration = parseInt(duration);
    return !isNaN(numDuration) && numDuration >= 0 && numDuration <= 100;
  };

  const validateOwnershipType = (type) => {
    return ['Owned', 'Rented'].includes(type);
  };

  const validateMobileNumber = (mobile) => {
    const mobileRegex = /^[6-9][0-9]{9}$/;
    return mobileRegex.test(mobile);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('handleChange called with:', { name, value });
    
    let processedValue = value;
    
    // Process PAN number to uppercase
    if (name === 'panNumber') {
      processedValue = value.toUpperCase();
    }
    
    // Process mobile number - only allow digits and limit to 10 digits
    if (name === 'mobile') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }
    
    // Process PIN code - only allow digits and limit to 6 digits
    if (name === 'currentAddress.pincode' || name === 'permanentAddress.pincode') {
        processedValue = value.replace(/\D/g, '').slice(0, 6);
      }
    
    
    // Update store - handle nested field names
    if (name.includes('.')) {
      updatePersonalDetail(name, processedValue);
      
      // If updating current address and "same as current" is selected, copy to permanent address
      if (name.startsWith('currentAddress.') && personalDetails.isSameAsCurrent) {
        const fieldName = name.replace('currentAddress.', 'permanentAddress.');
        updatePersonalDetail(fieldName, processedValue);
      }
    } else {
      updatePersonalDetail(name, processedValue);
    }
    
    // Clear error when user starts typing
    if (errors.personal[name]) {
      const newErrors = { ...errors.personal };
      delete newErrors[name];
      setErrors('personal', newErrors);
    }

    // Real-time validation for each field
    // Real-time validation for all fields (with small delay to ensure store is updated)
    setTimeout(() => {
      validateField(name, processedValue);
    }, 10);
  };

  // Real-time field validation
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors.personal };
    
    switch (fieldName) {
      case 'fullName':
        if (!value || value.trim().length < 2) {
          newErrors.fullName = 'Full name must be at least 2 characters';
        } else {
          delete newErrors.fullName;
        }
        break;

      case 'panNumber':
        if (!value) {
          newErrors.panNumber = 'PAN number is required';
        } else if (!validatePAN(value)) {
          newErrors.panNumber = 'Please enter a valid PAN number';
        } else {
          delete newErrors.panNumber;
        }
        break;

      case 'gender':
        if (!value) {
          newErrors.gender = 'Please select your gender';
        } else if (!validateGender(value)) {
          newErrors.gender = 'Please select a valid gender';
        } else {
          delete newErrors.gender;
        }
        break;

       case 'emailId':
         if (!value) {
           newErrors.emailId = 'Email is required';
         } else if (!validateEmail(value)) {
           newErrors.emailId = 'Please enter a valid email address';
         } else {
           delete newErrors.emailId;
         }
         break;

        case 'mobile':
          if (!value) {
            newErrors.mobile = 'Mobile number is required';
          } else if (!validateMobileNumber(value)) {
            newErrors.mobile = 'Please enter a valid 10-digit mobile number';
          } else {
            delete newErrors.mobile;
          }
          break;

      case 'currentAddress.addressLine':
        if (!value || value.trim().length < 10) {
          newErrors['currentAddress.addressLine'] = 'Address must be at least 10 characters';
        } else {
          delete newErrors['currentAddress.addressLine'];
        }
        break;

      case 'currentAddress.city':
        if (!value || value.trim().length < 2) {
          newErrors['currentAddress.city'] = 'City must be at least 2 characters';
        } else {
          delete newErrors['currentAddress.city'];
        }
        break;

      case 'currentAddress.state':
        if (!value || value.trim().length < 2) {
          newErrors['currentAddress.state'] = 'State must be at least 2 characters';
        } else {
          delete newErrors['currentAddress.state'];
        }
        break;

      case 'currentAddress.pincode':
        if (!value) {
          newErrors['currentAddress.pincode'] = 'PIN code is required';
        } else if (value.length < 6) {
          // Don't show error until user has entered 6 digits
          delete newErrors['currentAddress.pincode'];
        } else if (!validatePinCode(value)) {
          newErrors['currentAddress.pincode'] = 'Please enter a valid 6-digit PIN code';
        } else {
          delete newErrors['currentAddress.pincode'];
        }
        break;

      case 'aadharNumber':
        if (!value) {
          newErrors.aadharNumber = 'Aadhar number is required';
        } else if (!validateAadhar(value)) {
          newErrors.aadharNumber = 'Please enter a valid 12-digit Aadhar number';
        } else {
          delete newErrors.aadharNumber;
        }
        break;

       case 'dateOfBirth':
         if (!value) {
           newErrors.dateOfBirth = 'Date of birth is required';
         } else if (!validateDateOfBirth(value)) {
           newErrors.dateOfBirth = 'Age must be between 18 and 100 years';
         } else {
           delete newErrors.dateOfBirth;
         }
         break;

       case 'country':
         if (!value || value.trim().length < 2) {
           newErrors.country = 'Country must be at least 2 characters';
         } else {
           delete newErrors.country;
         }
         break;

       case 'building':
         if (!value || value.trim().length < 10 || value.trim().length > 512) {
           newErrors.building = 'Building/Locality/Street must be at least 10 characters';
         } else {
           delete newErrors.building;
         }
         break;

      case 'currentAddress.landmark':
        if (!value || value.trim().length < 10) {
          newErrors['currentAddress.landmark'] = 'Landmark is required and must be at least 10 characters';
        } else {
          delete newErrors['currentAddress.landmark'];
        }
        break;

        case 'currentResidenceDurationYears':
          if (!value) {
            newErrors.currentResidenceDurationYears = 'Staying duration is required';
          } else if (!validateStayingDuration(value)) {
            newErrors.currentResidenceDurationYears = 'Please enter a valid duration (0-100 years)';
          } else {
            delete newErrors.currentResidenceDurationYears;
          }
          break;

        case 'currentResidenceType':
          if (!value) {
            newErrors.currentResidenceType = 'Ownership type is required';
          } else if (!validateOwnershipType(value)) {
            newErrors.currentResidenceType = 'Please select a valid ownership type';
          } else {
            delete newErrors.currentResidenceType;
          }
          break;

       // Permanent address validations
       case 'permanentCountry':
         if (!value || value.trim().length < 2) {
           newErrors.permanentCountry = 'Country must be at least 2 characters';
         } else {
           delete newErrors.permanentCountry;
         }
         break;

       case 'permanentAddress.pincode':
         if (!value) {
           newErrors['permanentAddress.pincode'] = 'PIN code is required';
         } else if (value.length < 6) {
           // Don't show error until user has entered 6 digits
           delete newErrors['permanentAddress.pincode'];
         } else if (!validatePinCode(value)) {
           newErrors['permanentAddress.pincode'] = 'Please enter a valid 6-digit PIN code';
         } else {
           delete newErrors['permanentAddress.pincode'];
         }
         break;

       case 'permanentAddress.state':
         if (!value || value.trim().length < 2) {
           newErrors['permanentAddress.state'] = 'State must be at least 2 characters';
         } else {
           delete newErrors['permanentAddress.state'];
         }
         break;

       case 'permanentAddress.city':
         if (!value || value.trim().length < 2) {
           newErrors['permanentAddress.city'] = 'City must be at least 2 characters';
         } else {
           delete newErrors['permanentAddress.city'];
         }
         break;

       case 'permanentAddress.addressLine':
         if (!value || value.trim().length < 10 || value.trim().length > 512) {
           newErrors['permanentAddress.addressLine'] = 'Building/Locality/Street must be at least 10 characters';
         } else {
           delete newErrors['permanentAddress.addressLine'];
         }
         break;

       case 'permanentAddress.landmark':
         if (!value || value.trim().length < 10) {
           newErrors['permanentAddress.landmark'] = 'Landmark is required and must be at least 10 characters';
         } else {
           delete newErrors['permanentAddress.landmark'];
         }
         break;

       default:
         break;
    }

    setErrors('personal', newErrors);
  };

  // Handle current/permanent address toggle
  const handleAddressTypeChange = (e) => {
    const { value } = e.target;
    const isSame = value === 'same';
    updatePersonalDetail('isSameAsCurrent', isSame);
    
    if (isSame) {
      // Copy current address details to permanent address
      const currentAddress = personalDetails.currentAddress;
      updatePersonalDetail('permanentAddress.addressLine', currentAddress.addressLine);
      updatePersonalDetail('permanentAddress.city', currentAddress.city);
      updatePersonalDetail('permanentAddress.state', currentAddress.state);
      updatePersonalDetail('permanentAddress.country', currentAddress.country);
      updatePersonalDetail('permanentAddress.pincode', currentAddress.pincode);
      updatePersonalDetail('permanentAddress.landmark', currentAddress.landmark);
      
      setShowPermanentAddress(false);
    } else {
      setShowPermanentAddress(true);
    }
  };

  // Handle next button click
  const handleNext = () => {
    if (!isPersonalDetailsValid()) {
      showToast('Please fill all required fields correctly', 'error');
      return;
    }
    
    // Show confirmation modal first
    setShowConfirmationModal(true);
  };

  const handleConfirmNext = async () => {
    setShowConfirmationModal(false);
    setIsSaving(true);
    try {
      // Prepare data in API format
      const apiData = {
        leadId: leadData.id,
        fullName: personalDetails.fullName,
        panNumber: personalDetails.panNumber,
        gender: personalDetails.gender,
        emailId: personalDetails.emailId,
        aadharNumber: personalDetails.aadharNumber,
        dateOfBirth: personalDetails.dateOfBirth,
        mobile: personalDetails.mobile,
        permanentAddress: {
          addressLine: personalDetails.permanentAddress.addressLine,
          city: personalDetails.permanentAddress.city,
          state: personalDetails.permanentAddress.state,
          country: personalDetails.permanentAddress.country || 'India',
          pincode: personalDetails.permanentAddress.pincode,
          landmark: personalDetails.permanentAddress.landmark
        },
        currentAddress: personalDetails.isSameAsCurrent ? {
          addressLine: personalDetails.permanentAddress.addressLine,
          city: personalDetails.permanentAddress.city,
          state: personalDetails.permanentAddress.state,
          country: personalDetails.permanentAddress.country || 'India',
          pincode: personalDetails.permanentAddress.pincode,
          landmark: personalDetails.permanentAddress.landmark
        } : {
          addressLine: personalDetails.currentAddress.addressLine,
          city: personalDetails.currentAddress.city,
          state: personalDetails.currentAddress.state,
          country: personalDetails.currentAddress.country || 'India',
          pincode: personalDetails.currentAddress.pincode,
          landmark: personalDetails.currentAddress.landmark
        },
        isSameAsCurrent: personalDetails.isSameAsCurrent,
        currentResidenceDurationYears: personalDetails.currentResidenceDurationYears ? parseInt(personalDetails.currentResidenceDurationYears) : null,
        currentResidenceType: personalDetails.currentResidenceType
      };

      console.log('Sending personal details to API:', apiData);
      
      const response = await fetch(`${CREDIT_API_URL}/personal-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });
      
      console.log('API Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        if (result.success || result.status === 'Success') {
          // Check for cashePreApproval
          if (result.data && result.data.cashePreApproval && result.data.cashePreApproval.success) {
            setPreApprovedAmount(result.data.cashePreApproval.casheResponse.payLoad.amount);
            setCashePlans(result.data.cashePreApproval.cashePlans.payLoad);
            setShowLoanPlanModal(true); // Show the new loan plan modal
          } else if (result.data && result.data.cashePreApproval && result.data.cashePreApproval.redirectToTrustPaisa === true) {
            // CASHe failed but redirectToTrustPaisa is true - try alternative lender allocation
            console.log('CASHe pre-approval failed with redirectToTrustPaisa: true, attempting alternative lender allocation');
            await allocateAlternativeLender();
          } else if (result.data && result.data.cashePreApproval && (result.data.cashePreApproval.status === 'rejected' || result.data.cashePreApproval.success === false)) {
            // Update to use the new rejection modal states
            setRejectionModalMessage('Sorry, you can\'t proceed further as your application is rejected from one of our lending partners.');
            setShowRejectionModal(true);
          } else {
            showToast('Personal details saved successfully', 'success');
            onNext(); // Move to next step if no pre-approval or pre-approval not successful
          }
        } else {
          showToast(result.message || 'Failed to save personal details', 'error');
          // Do not move to the next step
        }
      } else {
        let errorResult;
        try {
          errorResult = await response.json();
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorResult = { message: 'Failed to save personal details' };
        }
        
        console.error('API Error Response:', errorResult);
        console.error('Response Status:', response.status);
        console.error('Response Headers:', response.headers);
        
        const serverMessage = errorResult?.message || errorResult?.error || 'Failed to save personal details';
        
        // Show error message as toast notification
        console.log('Showing toast with message:', serverMessage);
        showToast(serverMessage, 'error');
        
        // Map field-specific error to form if provided (for field highlighting)
        if (errorResult?.field) {
          console.log('Mapping error to field:', errorResult.field);
          const updatedErrors = { ...errors.personal, [errorResult.field]: serverMessage };
          setErrors('personal', updatedErrors);
        }
      }
    } catch (error) {
      console.error('Error saving personal details:', error);
      showToast('Error saving personal details', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-fill address based on PIN code using postal PIN code API
  const handlePinCodeChange = async (e) => {
    const { value } = e.target;
    handleChange(e); // Call the regular handleChange first
    
    // If PIN code is valid, try to auto-fill address details
    if (validatePinCode(value)) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await response.json();
        console.log('Current address PIN code API response:', data);
        
        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice) {
          const postOffice = data[0].PostOffice[0];
          const city = postOffice.District || postOffice.Name;
          const state = postOffice.State;
          console.log('Auto-filling current address:', { city, state });
          
          // Update current address city and state using Zustand store
          updatePersonalDetail('currentAddress.city', city);
          updatePersonalDetail('currentAddress.state', state);
          
          // If "same as current" is selected, also update permanent address
          if (personalDetails.isSameAsCurrent) {
            updatePersonalDetail('permanentAddress.city', city);
            updatePersonalDetail('permanentAddress.state', state);
          }
          
          // Clear any existing errors for current address city and state
          const newErrors = { ...errors.personal };
          delete newErrors['currentAddress.city'];
          delete newErrors['currentAddress.state'];
          if (personalDetails.isSameAsCurrent) {
            delete newErrors['permanentAddress.city'];
            delete newErrors['permanentAddress.state'];
          }
          setErrors('personal', newErrors);
          
          showToast(`Address auto-filled: ${city}, ${state}`, 'success');
        } else {
          console.log('No data found for PIN code:', value);
        }
      } catch (error) {
        console.error('Error fetching PIN code data:', error);
        showToast('Unable to auto-fill address. Please enter manually.', 'warning');
      }
    }
  };

  // Auto-fill permanent address based on PIN code
  const handlePermanentPinCodeChange = async (e) => {
    const { value } = e.target;
    handleChange(e); // Call the regular handleChange first
    
    // If PIN code is valid, try to auto-fill address details
    if (validatePinCode(value)) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await response.json();
        console.log('Permanent address PIN code API response:', data);
        
        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice) {
          const postOffice = data[0].PostOffice[0];
          const city = postOffice.District || postOffice.Name;
          const state = postOffice.State;
          console.log('Auto-filling permanent address:', { city, state });
          
          // Update permanent address city and state using Zustand store
          updatePersonalDetail('permanentAddress.city', city);
          updatePersonalDetail('permanentAddress.state', state);
          
          // Clear any existing errors for permanent address city and state
          const newErrors = { ...errors.personal };
          delete newErrors['permanentAddress.city'];
          delete newErrors['permanentAddress.state'];
          setErrors('personal', newErrors);
          
          showToast(`Permanent address auto-filled: ${city}, ${state}`, 'success');
        } else {
          console.log('No data found for permanent PIN code:', value);
        }
      } catch (error) {
        console.error('Error fetching permanent PIN code data:', error);
        showToast('Unable to auto-fill permanent address. Please enter manually.', 'warning');
      }
    }
  };

  // Add new functions for the rejection modal
  const handleRejectionModalClose = () => {
    setShowRejectionModal(false);
  };

  const handleRejectionModalConfirm = () => {
    setShowRejectionModal(false);
    navigate('/'); // Redirect to homepage
  };

  // Helper function to map employment type for allocation API
  const mapEmploymentType = (employmentType) => {
    if (!employmentType) return 'Salaried';
    const lower = employmentType.toLowerCase();
    if (lower.includes('salaried') || lower === 'salaried') return 'Salaried';
    if (lower.includes('self') || lower.includes('self-employed')) return 'Self Employed';
    if (lower.includes('business')) return 'Business';
    return 'Salaried';
  };

  // Helper function to map salary received type for allocation API
  const mapSalaryReceivedType = (salaryReceivedType) => {
    if (!salaryReceivedType) return 'Bank';
    const lower = salaryReceivedType.toLowerCase();
    if (lower.includes('direct') || lower.includes('bank')) return 'Bank';
    if (lower.includes('cheque')) return 'Cheque';
    if (lower.includes('cash')) return 'Cash';
    return 'Bank';
  };

  // Collect required data for lender allocation API
  const collectAllocationData = async () => {
    const allocationData = {
      leadId: leadData?.id,
      pinCode: personalDetails.currentAddress?.pincode || personalDetails.permanentAddress?.pincode || leadData?.pincode,
      loanAmount: leadData?.loanAmount || 0,
      loanType: 'Personal Loan',
      dateOfBirth: personalDetails.dateOfBirth,
      excludeLenderId: 'CASHE_001'
    };

    // Try to get employment data from professional details API
    try {
      if (leadData?.id) {
        const profResponse = await fetch(`${CREDIT_API_URL}/professional-details?leadId=${leadData.id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (profResponse.ok) {
          const profResult = await profResponse.json();
          if (profResult.status === 'Success' && profResult.data) {
            allocationData.employmentType = mapEmploymentType(profResult.data.employmentType);
            allocationData.monthlySalary = parseInt(profResult.data.netMonthlyIncome) || 0;
            allocationData.salaryReceivedType = mapSalaryReceivedType(profResult.data.salaryReceivedType);
            return allocationData;
          }
        }
      }
    } catch (error) {
      console.warn('Could not fetch professional details:', error);
    }

    // Fallback: Try to get from eligibility data in leadData
    if (leadData?.eligibility?.inputs) {
      const eligibilityInputs = leadData.eligibility.inputs;
      allocationData.employmentType = mapEmploymentType(eligibilityInputs.employmentType);
      allocationData.monthlySalary = parseInt(eligibilityInputs.monthlySalary) || 0;
      allocationData.salaryReceivedType = mapSalaryReceivedType(eligibilityInputs.salaryReceivedType);
      return allocationData;
    }

    // If no employment data found, return what we have (allocation API will handle missing fields)
    return allocationData;
  };

  // Call alternative lender allocation API
  const allocateAlternativeLender = async () => {
    setIsAllocatingAlternativeLender(true);
    
    try {
      const allocationData = await collectAllocationData();
      
      // Validate required fields
      if (!allocationData.leadId || !allocationData.pinCode || !allocationData.dateOfBirth) {
        console.error('Missing required data for allocation:', allocationData);
        throw new Error('Required data missing for lender allocation');
      }

      console.log('Calling alternative lender allocation API with data:', allocationData);

      const response = await fetch(`${CREDIT_API_URL}/lender/allocate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(allocationData)
      });

      const result = await response.json();
      console.log('Alternative lender allocation response:', result);

      if (result.eligible && result.lenderId && result.lenderLink) {
        // Alternative lender found - show success and redirect
        const lenderName = result.lenderName || 'alternative lender';
        
        // Show success toast
        showToast(`Your application with CASHe was not approved. We've found an alternative lender (${lenderName}) for you. Redirecting in 3 seconds...`, 'success');
        
        // Wait 3 seconds before redirect
        setTimeout(() => {
          // Store redirect info
          const redirectInfo = {
            goingToLender: true,
            timestamp: Date.now(),
            lenderName: lenderName
          };
          localStorage.setItem('snapfunds_previous_page', JSON.stringify(redirectInfo));
          
          // Redirect to alternative lender
          window.location.href = result.lenderLink;
        }, 3000);
      } else {
        // No alternative lender found - show rejection modal
        setRejectionModalMessage('Sorry, you can\'t proceed further as your application is rejected from one of our lending partners.');
        setShowRejectionModal(true);
      }
    } catch (error) {
      console.error('Error in alternative lender allocation:', error);
      // On error, show rejection modal
      setRejectionModalMessage('Sorry, you can\'t proceed further as your application is rejected from one of our lending partners.');
      setShowRejectionModal(true);
    } finally {
      setIsAllocatingAlternativeLender(false);
    }
  };

  const handlePlanSelected = async (selectedPlan) => {
    setIsProcessingPlan(true); // Set loading state for plan selection
    try {
      // Replace spaces with underscores in loanType if needed (e.g., "CASHe 270" becomes "CASHe_270")
      const processedLoanType = selectedPlan.loanType.replace(/\s/g, '_');
      
      const payload = {
        leadId: leadData.id,
        productTypeName: processedLoanType
      };

      console.log('Sending loan plan selection to API:', payload);

      const response = await fetch(`${CREDIT_API_URL}/personal-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('API Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        showToast('Loan plan selected and processed successfully!', 'success');
        onNext(); // Move to professional details page
      } else {
        const errorResult = await response.json();
        console.error('API Error Response:', errorResult);
        showToast(errorResult?.message || 'Failed to process loan plan.', 'error');
      }
    } catch (error) {
      console.error('Error processing loan plan:', error);
      showToast('Error processing loan plan.', 'error');
    } finally {
      setIsProcessingPlan(false); // Reset loading state
      setShowLoanPlanModal(false); // Close the modal
    }
  };

  if (isLoading) {
    return (
      <div className="onboard-step">
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading personal details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboard-step">
      <h3 style={{ marginBottom: '20px', color: '#283782' }}>Your personal details will help us to check available loan products.</h3>
      
      
      <Form id="personal-details-form">
                {/* Full Name and Date of Birth */}
                <div className="input-row">
                  <div className={`floating-input-group ${errors.personal.fullName ? 'error' : ''}`}>
                    <input
                      type="text"
                      name="fullName"
                      id="fullName"
                      value={personalDetails.fullName}
                      onChange={handleChange}
                      className="floating-input"
                      placeholder=" "
                    />
                    <label htmlFor="fullName" className="floating-label">Enter Your Full Name as per Aadhar</label>
                    {errors.personal.fullName && (
                      <div className="field-error">{errors.personal.fullName}</div>
                    )}
                  </div>

                  <div className={`floating-input-group ${errors.personal.dateOfBirth ? 'error' : ''}`}>
                    <input
                      type="date"
                      name="dateOfBirth"
                      id="dateOfBirth"
                      value={personalDetails.dateOfBirth}
                      onChange={handleChange}
                      className="floating-input"
                      placeholder=" "
                      disabled
                      style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
                    />
                    <label htmlFor="dateOfBirth" className="floating-label">Enter Your Date of Birth</label>
                    {errors.personal.dateOfBirth && (
                      <div className="field-error">{errors.personal.dateOfBirth}</div>
                    )}
                  </div>
                </div>

                 {/* Gender and Mobile Number */}
                 <div className="input-row">
                   <div className={`floating-input-group gender-field-group ${errors.personal.gender ? 'error' : ''}`}>
                     <div className="gender-field-content">
                       <label className="gender-label">Select Your Gender</label>
                       <div className="gender-radio-group">
                         {['Male', 'Female', 'Other'].map((option) => (
                           <div key={option} className="radio-option gender-radio-option">
                             <input
                               type="radio"
                               id={`gender-${option}`}
                        name="gender"
                               value={option}
                               checked={personalDetails.gender === option}
                        onChange={handleChange}
                               className="radio-input"
                             />
                             <label htmlFor={`gender-${option}`} className="radio-label">
                               {option}
                             </label>
                           </div>
                         ))}
                       </div>
                     </div>
                     {errors.personal.gender && (
                       <div className="field-error">{errors.personal.gender}</div>
                     )}
                   </div>
                    <div className={`floating-input-group ${errors.personal.mobile ? 'error' : ''}`}>
                      <input
                        type="tel"
                        name="mobile"
                        id="mobile"
                        value={personalDetails.mobile}
                        onChange={handleChange}
                        className="floating-input"
                        placeholder=" "
                        maxLength="10"
                        disabled
                        style={{ backgroundColor: '#f8f9fa', color: '#6c757d', cursor: 'not-allowed' }}
                      />
                      <label htmlFor="mobile" className="floating-label">Enter Your Mobile Number</label>
                      {errors.personal.mobile && (
                        <div className="field-error">{errors.personal.mobile}</div>
                      )}
                    </div>
                 </div>

                 {/* Email and PAN Number */}
                 <div className="input-row">
                 <div className={`floating-input-group ${errors.personal.emailId ? 'error' : ''}`}>
                     <input
                       type="email"
                       name="emailId"
                       id="emailId"
                       value={personalDetails.emailId}
                       onChange={handleChange}
                       className="floating-input"
                       placeholder=" "
                     />
                     <label htmlFor="emailId" className="floating-label">Enter Your Email Address</label>
                     {errors.personal.emailId && (
                       <div className="field-error">{errors.personal.emailId}</div>
                     )}
                   </div>
                   <div className={`floating-input-group ${errors.personal.panNumber ? 'error' : ''}`}>
                    <input
                      type="text"
                      name="panNumber"
                      id="panNumber"
                      value={personalDetails.panNumber}
                      onChange={handleChange}
                      className="floating-input"
                      placeholder=" "
                      maxLength="10"
                    />
                    <label htmlFor="panNumber" className="floating-label">Enter Your PAN Number</label>
                    {errors.personal.panNumber && (
                      <div className="field-error">{errors.personal.panNumber}</div>
                    )}
                  </div>
                 </div>

                 {/* Aadhar Number */}
                <div className="input-row">
                  <div className={`floating-input-group ${errors.personal.aadharNumber ? 'error' : ''}`}>
                    <input
                      type="text"
                      name="aadharNumber"
                      id="aadharNumber"
                      value={personalDetails.aadharNumber}
                      onChange={handleChange}
                      className="floating-input"
                      placeholder=" "
                      maxLength="12"
                    />
                    <label htmlFor="aadharNumber" className="floating-label">Enter Your Aadhar Number</label>
                    {errors.personal.aadharNumber && (
                      <div className="field-error">{errors.personal.aadharNumber}</div>
                    )}
                  </div>
                   <div></div>
                </div>

                 {/* Current Address Section */}
                 <h4 style={{ marginTop: '32px', marginBottom: '16px', color: '#283782' }}>Current Address</h4>
                 
                 {/* Country and PIN Code */}
                 <div className="input-row">
                   <div className={`floating-input-group ${errors.personal.country ? 'error' : ''}`}>
                     <input
                       type="text"
                       name="country"
                       id="country"
                       value={personalDetails.country || 'India'}
                       onChange={handleChange}
                       className="floating-input"
                       placeholder=" "
                     />
                     <label htmlFor="country" className="floating-label">Enter Your Country</label>
                     {errors.personal.country && (
                       <div className="field-error">{errors.personal.country}</div>
                     )}
                   </div>

                    <div className={`floating-input-group ${errors.personal['currentAddress.pincode'] ? 'error' : ''}`}>
                      <input
                        type="text"
                        name="currentAddress.pincode"
                        id="currentPinCode"
                        value={personalDetails.currentAddress?.pincode || ''}
                        onChange={handlePinCodeChange}
                        className="floating-input"
                        placeholder=" "
                        maxLength="6"
                        readOnly={!!personalDetails.currentAddress?.pincode}
                        style={personalDetails.currentAddress?.pincode ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                      />
                      <label htmlFor="currentPinCode" className="floating-label">Enter Your PIN Code</label>
                      {errors.personal['currentAddress.pincode'] && (
                        <div className="field-error">{errors.personal['currentAddress.pincode']}</div>
                      )}
                    </div>
                 </div>

                 {/* State and City */}
                 <div className="input-row">
                   <div className={`floating-input-group ${errors.personal['currentAddress.state'] ? 'error' : ''}`}>
                     <input
                       type="text"
                       name="currentAddress.state"
                       id="currentState"
                       value={personalDetails.currentAddress?.state || ''}
                       onChange={handleChange}
                       className="floating-input"
                       placeholder=" "
                       readOnly={isLoadingPincodeDetails || (pincodeDistricts.length > 0)}
                       style={isLoadingPincodeDetails || (pincodeDistricts.length > 0) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                     />
                     <label htmlFor="currentState" className="floating-label">Enter Your State</label>
                     {errors.personal['currentAddress.state'] && (
                       <div className="field-error">{errors.personal['currentAddress.state']}</div>
                     )}
                   </div>

                   <div className={`floating-input-group ${errors.personal['currentAddress.city'] ? 'error' : ''}`} style={{ position: 'relative' }}>
                     {pincodeDistricts.length > 0 ? (
                       <>
                         <select
                           id="currentCity"
                           value={personalDetails.currentAddress?.city || (pincodeDistricts.length > 0 && [...new Set(pincodeDistricts.map(item => item.district))].length === 1 ? [...new Set(pincodeDistricts.map(item => item.district))][0] : '')}
                           onChange={(e) => handleDistrictSelect(e.target.value)}
                           className={`floating-input ${[...new Set(pincodeDistricts.map(item => item.district))].length === 1 ? 'single-option-select' : ''}`}
                           style={{ paddingTop: '20px', paddingBottom: '8px' }}
                         >
                           {[...new Set(pincodeDistricts.map(item => item.district))].length === 1 ? (
                             // If only one option, show it directly without "Select District" placeholder and auto-select it
                             [...new Set(pincodeDistricts.map(item => item.district))].map((district, index) => (
                               <option key={index} value={district}>{district}</option>
                             ))
                           ) : (
                             <>
                               <option value="">Select District</option>
                               {[...new Set(pincodeDistricts.map(item => item.district))].map((district, index) => (
                                 <option key={index} value={district}>{district}</option>
                               ))}
                             </>
                           )}
                         </select>
                         <label htmlFor="currentCity" className="floating-label">Enter Your City (District)</label>
                       </>
                     ) : (
                       <>
                     <input
                       type="text"
                       name="currentAddress.city"
                       id="currentCity"
                       value={personalDetails.currentAddress?.city || ''}
                       onChange={handleChange}
                       className="floating-input"
                       placeholder=" "
                           readOnly={isLoadingPincodeDetails}
                           style={isLoadingPincodeDetails ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                     />
                     <label htmlFor="currentCity" className="floating-label">Enter Your City</label>
                       </>
                     )}
                     {errors.personal['currentAddress.city'] && (
                       <div className="field-error">{errors.personal['currentAddress.city']}</div>
                     )}
                     {isLoadingPincodeDetails && (
                       <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#666' }}>
                         Loading...
                       </div>
                     )}
                   </div>
                 </div>

                 {/* Building/Locality/Street */}
                 <div className="input-row">
                    <div className={`floating-input-group ${errors.personal['currentAddress.addressLine'] ? 'error' : ''}`}>
                       <input
                         type="text"
                         name="currentAddress.addressLine"
                         id="currentAddressLine"
                         value={personalDetails.currentAddress?.addressLine || ''}
                         onChange={handleChange}
                         className="floating-input"
                         placeholder=" "
                       />
                       <label htmlFor="currentAddressLine" className="floating-label">Enter Your Address</label>
                       {errors.personal['currentAddress.addressLine'] && (
                         <div className="field-error">{errors.personal['currentAddress.addressLine']}</div>
                       )}
                     </div>
                     <div className={`floating-input-group ${errors.personal['currentAddress.landmark'] ? 'error' : ''}`}>
                       <input
                         type="text"
                         name="currentAddress.landmark"
                         id="currentLandmark"
                         value={personalDetails.currentAddress?.landmark || ''}
                         onChange={handleChange}
                         className="floating-input"
                         placeholder=" "
                       />
                       <label htmlFor="currentLandmark" className="floating-label">Enter Your Landmark</label>
                       {errors.personal['currentAddress.landmark'] && (
                         <div className="field-error">{errors.personal['currentAddress.landmark']}</div>
                       )}
                     </div>
                 </div>

                  {/* Staying Duration and Ownership Type */}
                  <div className="input-row">
                    <div className={`floating-input-group ${errors.personal.currentResidenceDurationYears ? 'error' : ''}`}>
                      <input
                        type="number"
                        name="currentResidenceDurationYears"
                        id="currentResidenceDurationYears"
                        value={personalDetails.currentResidenceDurationYears}
                        onChange={handleChange}
                        className="floating-input"
                        placeholder=" "
                        min="0"
                        max="100"
                      />
                      <label htmlFor="currentResidenceDurationYears" className="floating-label">How long have you been staying at your current address? (Years)</label>
                      {errors.personal.currentResidenceDurationYears && (
                        <div className="field-error">{errors.personal.currentResidenceDurationYears}</div>
                      )}
                    </div>

                    <div className={`floating-input-group ${errors.personal.currentResidenceType ? 'error' : ''}`}>
                      <select
                        name="currentResidenceType"
                        id="currentResidenceType"
                        value={personalDetails.currentResidenceType}
                        onChange={handleChange}
                        className="floating-select"
                      >
                        <option value="">Select Ownership Type</option>
                        <option value="Owned">Owned</option>
                        <option value="Rented">Rented</option>
                      </select>
                      <label htmlFor="currentResidenceType" className="floating-label">Is it owned or rented?</label>
                      {errors.personal.currentResidenceType && (
                        <div className="field-error">{errors.personal.currentResidenceType}</div>
                      )}
                    </div>
                 </div>

                  {/* Current/Permanent Address Question */}
                  <div className="input-row">
                    <div style={{ width: '100%', marginBottom: '16px' }}>
                      <label style={{ color: '#283782', fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '12px' ,'marginLeft':'10px'}}>
                        Is permanent address same as current address?
                      </label>
                      <div style={{ display: 'flex', gap: '20px' ,'marginLeft':'10px'}}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="addressType"
                            value="same"
                            checked={personalDetails.isSameAsCurrent === true}
                            onChange={handleAddressTypeChange}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span>Yes</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="addressType"
                            value="different"
                            checked={personalDetails.isSameAsCurrent === false}
                            onChange={handleAddressTypeChange}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>
                  </div>

                

                 {/* Permanent Address Section - Show only if different */}
                 {showPermanentAddress && (
                   <>
                     <h4 style={{ marginTop: '32px', marginBottom: '16px', color: '#283782' }}>Permanent Address</h4>
                     
                     {/* Permanent Country and PIN Code */}
                     <div className="input-row">
                       <div className={`floating-input-group ${errors.personal.permanentCountry ? 'error' : ''}`}>
                         <input
                           type="text"
                           name="permanentCountry"
                           id="permanentCountry"
                           value={personalDetails.permanentCountry || 'India'}
                           onChange={handleChange}
                           className="floating-input"
                           placeholder=" "
                         />
                         <label htmlFor="permanentCountry" className="floating-label">Enter Your Country</label>
                         {errors.personal.permanentCountry && (
                           <div className="field-error">{errors.personal.permanentCountry}</div>
                         )}
                       </div>

                       <div className={`floating-input-group ${errors.personal['permanentAddress.pincode'] ? 'error' : ''}`}>
                         <input
                           type="text"
                           name="permanentAddress.pincode"
                           id="permanentPinCode"
                           value={personalDetails.permanentAddress?.pincode || ''}
                           onChange={handlePermanentPinCodeChange}
                           className="floating-input"
                           placeholder=" "
                           maxLength="6"
                         />
                         <label htmlFor="permanentPinCode" className="floating-label">Enter Your PIN Code</label>
                         {errors.personal['permanentAddress.pincode'] && (
                           <div className="field-error">{errors.personal['permanentAddress.pincode']}</div>
                         )}
                       </div>
                     </div>

                     {/* Permanent State and City */}
                     <div className="input-row">
                       <div className={`floating-input-group ${errors.personal['permanentAddress.state'] ? 'error' : ''}`}>
                         <input
                           type="text"
                           name="permanentAddress.state"
                           id="permanentState"
                           value={personalDetails.permanentAddress?.state || ''}
                           onChange={handleChange}
                           className="floating-input"
                           placeholder=" "
                         />
                         <label htmlFor="permanentState" className="floating-label">Enter Your State</label>
                         {errors.personal['permanentAddress.state'] && (
                           <div className="field-error">{errors.personal['permanentAddress.state']}</div>
                         )}
                       </div>

                       <div className={`floating-input-group ${errors.personal['permanentAddress.city'] ? 'error' : ''}`}>
                         <input
                           type="text"
                           name="permanentAddress.city"
                           id="permanentCity"
                           value={personalDetails.permanentAddress?.city || ''}
                           onChange={handleChange}
                           className="floating-input"
                           placeholder=" "
                         />
                         <label htmlFor="permanentCity" className="floating-label">Enter Your City</label>
                         {errors.personal['permanentAddress.city'] && (
                           <div className="field-error">{errors.personal['permanentAddress.city']}</div>
                         )}
                       </div>
                     </div>

                     {/* Permanent Building/Locality/Street */}
                     <div className="input-row">
                       <div className={`floating-input-group ${errors.personal['permanentAddress.addressLine'] ? 'error' : ''}`}>
                         <input
                           type="text"
                           name="permanentAddress.addressLine"
                           id="permanentAddressLine"
                           value={personalDetails.permanentAddress?.addressLine || ''}
                           onChange={handleChange}
                           className="floating-input"
                           placeholder=" "
                         />
                         <label htmlFor="permanentAddressLine" className="floating-label">Enter Your Address</label>
                         {errors.personal['permanentAddress.addressLine'] && (
                           <div className="field-error">{errors.personal['permanentAddress.addressLine']}</div>
                         )}
                       </div>
                    
                       <div className={`floating-input-group ${errors.personal['permanentAddress.landmark'] ? 'error' : ''}`}>
                         <input
                           type="text"
                           name="permanentAddress.landmark"
                           id="permanentLandmark"
                           value={personalDetails.permanentAddress?.landmark || ''}
                           onChange={handleChange}
                           className="floating-input"
                           placeholder=" "
                         />
                         <label htmlFor="permanentLandmark" className="floating-label">Enter Your Landmark (Optional)</label>
                         {errors.personal['permanentAddress.landmark'] && (
                           <div className="field-error">{errors.personal['permanentAddress.landmark']}</div>
                         )}
                       </div>
                    
                     </div>

                     
                   </>
                 )}
              </Form>

              {/* Navigation Buttons */}
              <div className="wizard-nav">
                 <button 
                   className="btn-onboard-next" 
                   onClick={handleNext} 
                   disabled={isSubmitting || isSaving || !isPersonalDetailsValid() || isProcessingPlan || isAllocatingAlternativeLender}
                 >
                   {isSaving || isProcessingPlan || isAllocatingAlternativeLender 
                     ? (isAllocatingAlternativeLender ? 'Finding alternative lender...' : 'Saving...') 
                     : 'Next'} <i className="fas fa-arrow-right"></i>
                 </button>
              </div>

              <CasheLoanPlanModal
                show={showLoanPlanModal}
                onClose={handleCloseLoanPlanModal}
                preApprovedAmount={preApprovedAmount}
                cashePlans={cashePlans}
                onPlanSelect={handlePlanSelected}
              />

      {/* Rejection Modal */}
      <Modal show={showRejectionModal} onHide={handleRejectionModalClose} centered>
        <Modal.Body className="text-center p-4">
          <div className="warning-icon mb-3">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#ffc107' }}></i>
          </div>
          <p className="mb-4" style={{ fontSize: '16px', color: '#333' }}>
            {rejectionModalMessage}
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              onClick={handleRejectionModalClose}
              style={{ minWidth: '120px' }}
            >
              Stay Here
            </Button>
            <Button
              variant="warning"
              onClick={handleRejectionModalConfirm}
              style={{ minWidth: '120px' }}
            >
              Go to Homepage
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmationModal}
        onConfirm={handleConfirmNext}
        onCancel={() => setShowConfirmationModal(false)}
        isLoading={isSaving}
      />
    </div>
  );
};

export default PersonalDetailsStep;
